import MoneyInput from '../components/MoneyInput';
﻿import React, { useState, useEffect } from 'react';
import { DealColorPicker, DealTag } from '../components/DealColorPicker';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ContractImport, { ContractDetails, fillEmpty, highlightFields } from '../components/ContractImport';
import DocChecklistUpload, { uploadPendingDocs } from '../components/DocChecklistUpload';
import { SELLER_LISTING_DOCS } from '../shared/transactionDocs';
import TcAgentPicker, { isTcUser, withTcFields, loadDealAgents } from '../components/TcAgentPicker';
import { createTransaction, getTransaction, updateTransaction } from '../services/transactionService';
import { sendTransactionCreatedNotification } from '../services/notificationTriggerService';
import { generateTasksForTransaction } from '../services/taskService';
import { generateTeamTasks, syncTeamTaskDates } from '../services/transactionTasks';
import { sendSellerCreatedEmails } from '../services/listingNotifications';
import { formatPhone, formatWithCommas } from '../utils/formatters';
import { getAgents } from '../services/userService';
import { database } from '../firebaseConfig';
import { ref, onValue } from 'firebase/database';
import SourceSelect from '../components/SourceSelect';
import { useContactPrefill, linkContactToTransaction, usePipelineSource, markPipelineConverted } from '../services/contactLink';
import ClientPicker, { resolveClientContact } from '../components/ClientPicker';
import { getSellerInterest, markInterestConverted, interestPrice } from '../services/sellerInterestService';
import PhoneInput from '../components/PhoneInput';

import { cddToNumber } from '../utils/cdd';
const withCdd = (data) => ({ ...data, cddAnnual: cddToNumber(data.cddAnnual) });
// import notificationTriggerService - removed

export default function SellerForm() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const [editLoaded, setEditLoaded] = useState(!editId);
  const { user, userRole } = useAuth();
  // TC only (new deals): the deal belongs to the agent chosen at the top of the form.
  const isTC = isTcUser(userRole) && !isEdit;
  const [tcPick, setTcPick] = useState({ agentUid: '', coAgentUid: '' });
  const [tcSubmitTried, setTcSubmitTried] = useState(false);
  const [tcAgents, setTcAgents] = useState([]);
  useEffect(() => { if (isTC) loadDealAgents().then(setTcAgents).catch(() => setTcAgents([])); }, [isTC]);
  const ownerUid = isTC ? tcPick.agentUid : user?.uid;
  const tcError = isTC && !tcPick.agentUid ? 'Choose the agent this deal is for' : '';
  const asTc = (payload) => (isTC ? withTcFields(payload, 'Seller', tcPick, tcAgents, { uid: user?.uid, name: user?.displayName || '', email: user?.email || '' }) : payload);
  const [wasDraft, setWasDraft] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [picked, setPicked] = useState(null);
  const [photographers, setPhotographers] = useState([]);
  const [stagers, setStagers] = useState([]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const unsubscribe = onValue(ref(database, 'partners'), (snap) => {
      const val = snap.val() || {};
      const list = Object.values(val)
        .filter((p) => p && p.partnerType === 'Photographer')
        .map((p) => {
          const name = `${p.firstName || ''} ${p.lastName || ''}`.trim();
          return name || p.companyName || '';
        })
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      setPhotographers(list);
      const stagerList = Object.values(val)
        .filter((p) => p && p.partnerType === 'Stager')
        .map((p) => `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.companyName || '')
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
      setStagers(stagerList);
    }, (err) => console.error('Could not load photographers:', err));
    return () => unsubscribe();
  }, [user]);

  const [formData, setFormData] = useState({
    // Seller Information
    associationApprovalRequired: '',
    notifyHoaOfOpenHouses: '',
    hoaContactFirstName: '',
    hoaContactLastName: '',
    hoaContactEmail: '',
    cddAnnual: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',

    // Property Information
    propertyAddress: '',
    community: '',
    dealColor: '',
    city: '',
    state: '',
    zipCode: '',
    propertyType: 'single-family',

    // House notes: the single source Listing Launch writes from (MLS, brochure, mailer, feature list, feature cards).
    listingNotes: '',
    // Room facts for Listing Launch (MLS copy never states counts; den/flex/loft only when checked).
    bedrooms: '',
    fullBaths: '',
    halfBaths: '',
    hasDen: false,
    hasFlex: false,
    hasLoft: false,

    // Listing Preferences
    listingPrice: '',
    anticipatedListingDate: '',
    listingEffectiveDate: '',
    listingExpirationDate: '',

    // Staging & Photography
    stagingNeeded: 'no',
    photographerNotes: '',
    photographyDate: '',
    photographer: '',
    stager: '',

    // Marketing Materials
    createBrochure: true,
    createMailer: true,
    createFeatureCards: true,
    createFeatureList: true,
    createSocialGraphics: true,
    createJustSoldPostcards: false,
    marketingTheme: '',
    keyFeatures: '',
    tourNotes: '',
    rearDirection: '',

    // Showing Instructions
    occupancyStatus: '',
    accessInstructions: '',
    lockboxCode: '',

    // Commission Information
    commissionType: 'percentage', // 'percentage' or 'dollar'
    commissionPercentage: '',
    commissionDollarAmount: '',
    leadSource: 'agent-generated', // 'agent-generated' or 'team-generated'
    source: '',
    sourceOther: '',

    // Additional Information
    notes: ''
  });
  const linkedContact = useContactPrefill(setFormData, !editId);
  const pipelineSource = usePipelineSource(null, !editId);
  // Converting a Seller Interest: /transactions/new/seller?contact=<cid>&sellerInterest=<id> prefills the address and keeps the lead source.
  const sellerInterestId = !editId ? new URLSearchParams(window.location.search).get('sellerInterest') : null;
  useEffect(() => {
    if (!sellerInterestId) return;
    getSellerInterest(sellerInterestId).then((si) => {
      if (!si) return;
      const src = si.howHeard || '';
      const extraNotes = [
        si.community && `Community: ${si.community}`,
        si.floorPlan && `Floor plan: ${si.floorPlan}`,
        si.timeline && `Timeline: ${si.timeline}`,
        si.alsoBuying && `Also buying: ${si.alsoBuying}`,
        si.reasonForMoving && `Reason for moving: ${si.reasonForMoving}`,
        si.estimatedPrice && `Estimated price (seller interest): ${si.estimatedPrice}`,
        si.cma && `CMA: ${si.cma.fileName || 'created'}${si.cma.recommendedList ? ` (recommended ${si.cma.recommendedList})` : ''}`,
        si.notes && `Conversation notes: ${si.notes}`,
      ].filter(Boolean).join('\n');
      setFormData((prev) => ({
        ...prev,
        firstName: prev.firstName || si.firstName || '',
        lastName: prev.lastName || si.lastName || '',
        email: prev.email || si.email || '',
        phone: prev.phone || si.phone || '',
        propertyAddress: prev.propertyAddress || si.propertyAddress || '',
        listingPrice: prev.listingPrice || String(interestPrice(si) || ''),
        source: prev.source || (src ? src : ''),
        sourceOther: prev.sourceOther || (src === 'Other' ? (si.howHeardOther || '') : ''),
        community: si.community || '',
        floorPlan: si.floorPlan || '',
        cma: si.cma || null,
        notes: prev.notes || extraNotes,
        sellerInterestId,
        sellerInterestSource: si.leadSource || src || 'Seller interest',
      }));
      if ((si.additionalContacts || []).length) {
        setAdditionalContacts((prev) => (prev.length ? prev : si.additionalContacts.map((c, n) => ({
          id: Date.now() + n, firstName: c.firstName || '', lastName: c.lastName || '', role: c.relationship || '', phone: c.phone || '', email: c.email || '',
        }))));
      }
    }).catch((err) => console.error('Could not load seller interest', err));
  }, [sellerInterestId]);

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [pendingDocs, setPendingDocs] = useState({});
  const [errors, setErrors] = useState({});
  const [additionalContacts, setAdditionalContacts] = useState([]);
  const [coListAgents, setCoListAgents] = useState([]);
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    getAgents()
      .then(setAgents)
      .catch(err => console.error('Could not load agents:', err));
  }, []);

  const addCoListAgent = () => {
    setCoListAgents(prev => [...prev, { id: Date.now(), uid: '' }]);
  };

  const updateCoListAgent = (id, uid) => {
    setCoListAgents(prev => prev.map(a => (a.id === id ? { ...a, uid } : a)));
  };

  const removeCoListAgent = (id) => {
    setCoListAgents(prev => prev.filter(a => a.id !== id));
  };
  const [documentFiles, setDocumentFiles] = useState({});

  // Upload contract: fill only empty fields, highlight them; nothing saves until the agent presses Save.
  const applyContract = (f, mapped) => {
    const { filled } = fillEmpty(formData, mapped);
    setFormData((prev) => fillEmpty(prev, mapped).next);
    highlightFields(filled);
    return filled;
  };

  const [communityOther, setCommunityOther] = useState(false);
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let nextValue = type === 'checkbox' ? checked : value;
    if (name === 'phone') nextValue = formatPhone(value);
    if (['squareFootage', 'listingPrice', 'commissionDollarAmount', 'cddAnnual'].includes(name)) {
      nextValue = formatWithCommas(value);
    }
    setFormData(prev => ({
      ...prev,
      [name]: nextValue
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const condoDocuments = [
    { key: 'declaration', label: "Declaration of Condominium (including applicable amendments)" },
    { key: 'articles', label: "Articles of Incorporation of the Condominium Association" },
    { key: 'bylaws', label: "Association Bylaws" },
    { key: 'rulesRegulations', label: "Association Rules and Regulations" },
    { key: 'financialStatement', label: "Most Recent Annual Financial Statement" },
    { key: 'budget', label: "Current Annual Association Budget" },
    { key: 'faqDocument', label: "Frequently Asked Questions and Answers (Q&A) document" },
    { key: 'governanceForm', label: "Condominium Governance Form (Florida DBPR)" },
    { key: 'milestoneInspection', label: "Milestone Inspection Report, if applicable", note: "Required for condo buildings 3 stories or higher. Generally due at 30 years of building age (25 years if within 3 miles of the coast), then every 10 years. Local building officials can set earlier dates." },
    { key: 'sirs', label: "Structural Integrity Reserve Study (SIRS), if applicable", note: "Required for condo buildings 3 stories or higher. The association must have a current study on file, updated on the schedule set by state law. If the association has not completed one, get that statement in writing." },
    { key: 'turnoverInspection', label: "Turnover Inspection Report, if applicable", note: "Applies to condo buildings 3 stories or higher when the developer turns control over to unit owners, for turnovers on or after July 1, 2023. Mark not applicable if the building is under 3 stories or was never turned over." },
  ];
  // Notes are general guidance; confirm current requirements with the association or an attorney.

  const [notApplicable, setNotApplicable] = useState({});
  const NA_KEYS = ['milestoneInspection', 'sirs', 'turnoverInspection'];

  const handleDocumentUpload = (docKey, file) => {
    setDocumentFiles(prev => ({ ...prev, [docKey]: file ? file.name : '' }));
  };

  const emptyContact = { firstName: '', lastName: '', role: '', phone: '', email: '', signer: true };

  const addContact = () => {
    setAdditionalContacts(prev => [...prev, { ...emptyContact, id: Date.now() }]);
  };

  const updateContact = (id, field, value) => {
    const nextValue = field === 'phone' ? formatPhone(value) : value;
    setAdditionalContacts(prev =>
      prev.map(c => (c.id === id ? { ...c, [field]: nextValue } : c))
    );
  };

  const removeContact = (id) => {
    setAdditionalContacts(prev => prev.filter(c => c.id !== id));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.propertyAddress.trim()) newErrors.propertyAddress = 'Property address is required';
    if (!formData.listingPrice.trim()) newErrors.listingPrice = 'Listing price is required';
    if (!formData.anticipatedListingDate) newErrors.anticipatedListingDate = 'Anticipated listing date is required';
    if (!formData.listingEffectiveDate) newErrors.listingEffectiveDate = 'Listing agreement effective date is required';
    if (!formData.listingExpirationDate) newErrors.listingExpirationDate = 'Listing expiration date is required';

    return newErrors;
  };

  // Edit mode (route /transactions/:id/edit): load the saved seller deal into the form.
  useEffect(() => {
    if (!isEdit || !user) return;
    (async () => {
      try {
        const tx = await getTransaction(ownerUid, editId);
        if (tx) {
          setFormData(prev => ({
            ...prev,
            ...Object.fromEntries(Object.keys(prev).filter(k => tx[k] !== undefined && tx[k] !== null).map(k => [k, k === 'cddAnnual' ? formatWithCommas(String(tx[k])) : tx[k]])),
          }));
          setAdditionalContacts(tx.additionalContacts || []);
          setCoListAgents((tx.coListAgents || []).map(a => ({ uid: a.uid })));
          setWasDraft(!!tx.isDraft);
        }
      } catch (err) {
        console.error('Could not load transaction for editing', err);
        setSubmitError('Could not load this transaction.');
      } finally {
        setEditLoaded(true);
      }
    })();
  }, [isEdit, editId, user]);

  // Save as draft: no required fields, no tasks, no emails. "Continue draft" on the deal page reopens it here.
  const saveDraft = async () => {
    if (!user) return;
    if (isTC && !tcPick.agentUid) { setTcSubmitTried(true); window.scrollTo(0, 0); return; }
    setDraftSaving(true);
    setSubmitError('');
    try {
      const payload = { ...withCdd(formData), status: 'draft', isDraft: true, additionalContacts, coListAgents: coListAgents.filter(a => a.uid).map(a => { const agent = agents.find(x => x.uid === a.uid); return { uid: a.uid, name: agent?.displayName || '', email: agent?.email || '' }; }) };
      let txId = editId;
      if (isEdit) await updateTransaction(ownerUid, editId, payload);
      else txId = await createTransaction(ownerUid, asTc(payload), 'Seller');
      await uploadPendingDocs(ownerUid, txId, pendingDocs, user.displayName || user.email || '');
      navigate(`/transactions/${txId}`, { state: { saved: 'Draft saved. No tasks or emails were created. Use Continue draft to finish it.' } });
    } catch (err) {
      console.error('Could not save draft', err);
      setSubmitError('Could not save the draft. Please try again.');
    } finally {
      setDraftSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isTC && !tcPick.agentUid) { setTcSubmitTried(true); window.scrollTo(0, 0); return; }
    const newErrors = validateForm();

    if (Object.keys(newErrors).length === 0) {
      setLoading(true);
      setSubmitError('');
      setSubmitted(false);

      try {
        // Edit mode: save changes, then move task dates to match the current deal dates.
        if (isEdit && !wasDraft) {
          await updateTransaction(ownerUid, editId, {
            ...withCdd(formData),
            additionalContacts,
            coListAgents: coListAgents
              .filter(a => a.uid)
              .map(a => {
                const agent = agents.find(x => x.uid === a.uid);
                return { uid: a.uid, name: agent?.displayName || '', email: agent?.email || '' };
              }),
          });
          const fresh = await getTransaction(ownerUid, editId);
          await syncTeamTaskDates(editId, fresh);
          // Sends the professional staging email if staging was just set to professional. Failures never block the save.
          try {
            await sendSellerCreatedEmails(editId, fresh, ownerUid, ['Auto Email: Professional Staging Request']);
          } catch (err) {
            console.error('Could not send staging email', err);
          }
          navigate(`/transactions/${editId}`, { state: { saved: 'Changes saved.' } });
          return;
        }

        // Save to Firebase using the transactionService. A finished draft keeps its ID and now gets its tasks and emails.
        const sellerPayload = {
          ...withCdd(formData),
          status: 'incoming',
          isDraft: false,
          additionalContacts,
          documentFiles,
          coListAgents: coListAgents
            .filter(a => a.uid)
            .map(a => {
              const agent = agents.find(x => x.uid === a.uid);
              return { uid: a.uid, name: agent?.displayName || '', email: agent?.email || '' };
            }),
        };
        let transactionId = editId;
        if (wasDraft) await updateTransaction(ownerUid, editId, sellerPayload);
        else transactionId = await createTransaction(ownerUid, asTc(sellerPayload), 'Seller');
        const clientContact = await resolveClientContact(linkedContact || (picked?.contactId ? { id: picked.contactId } : null), formData, ownerUid, userRole === 'Admin', 'Seller');
        await markPipelineConverted(pipelineSource || picked?.source, 'Seller', transactionId, ownerUid);
        await linkContactToTransaction(clientContact, ownerUid, transactionId, 'Seller', [formData.firstName, formData.lastName].filter(Boolean).join(' '));
        if (sellerInterestId) {
          try { await markInterestConverted(sellerInterestId, transactionId, ownerUid); } catch (err) { console.error('Could not mark seller interest converted', err); }
        }

        // Generate tasks based on closing date (desiredClosingDate for Seller)
        if (transactionId) {
          await generateTeamTasks(transactionId, formData, ownerUid, 'Seller');

          // Get the transaction details and send created notification
          const transaction = await getTransaction(ownerUid, transactionId);
          if (transaction) {
            await sendTransactionCreatedNotification(ownerUid, transaction);
            // Seller auto emails (for example, New Listing to Start Working On). Failures never block the save.
            try {
              await sendSellerCreatedEmails(transactionId, transaction, ownerUid);
            } catch (err) {
              console.error('Could not send seller created emails', err);
            }
          }
        }

        console.log('Seller transaction saved to Firebase:', formData);
        setSubmitted(true);

        // Optional listing agreement goes to the deal's Documents.
        try { await uploadPendingDocs(ownerUid, transactionId, pendingDocs, user.displayName || user.email || ''); } catch (err) { console.error('Could not upload listing agreement', err); }
        navigate(`/transactions/${transactionId}`, { state: { saved: 'Seller transaction saved. Its tasks were created.' } });
      } catch (error) {
        console.error('Error saving seller transaction:', error);
        setSubmitError('Failed to save seller information. Please try again.');
        setLoading(false);
      }
    } else {
      setErrors(newErrors);
    }
  };

  if (isEdit && !editLoaded) return <div style={{ padding: 24 }}>Loading transaction...</div>;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate('/transactions/new')} style={styles.backButton}>
          <ArrowLeft size={20} /> Back
        </button>
        <h1 style={styles.title}>Seller Transaction</h1>
        <div style={{width: '80px'}}></div>
      </div>

      {/* Main Content */}
      <div style={styles.mainContent}>
        {submitted && (
          <div style={styles.successBox}>
            <CheckCircle size={24} style={{color: '#4CAF50'}} />
            <div>
              <p style={styles.successTitle}>Seller information saved!</p>
              <p style={styles.successSubtitle}>Your transaction has been created. Redirecting...</p>
            </div>
          </div>
        )}

        {submitError && (
          <div style={styles.errorBox}>
            <AlertCircle size={24} style={{color: '#d32f2f'}} />
            <div>
              <p style={styles.errorTitle}>Error saving transaction</p>
              <p style={styles.errorSubtitle}>{submitError}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <ContractImport kind="Seller" onApply={applyContract} />
          {(!isEdit || wasDraft) && <DocChecklistUpload title="Listing documents" docs={SELLER_LISTING_DOCS} pending={pendingDocs} onChange={setPendingDocs} />}
          <ContractDetails value={formData.contractDetails} onChange={(v) => setFormData(prev => ({ ...prev, contractDetails: v }))} />
          {isTC && <TcAgentPicker value={tcPick} onChange={setTcPick} agents={tcAgents} error={tcSubmitTried ? tcError : ''} />}
          <DealColorPicker value={formData.dealColor} onChange={(c) => setFormData(prev => ({ ...prev, dealColor: c }))} />
          {/* Seller Information Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Seller Information</h2>
            {!isEdit && <ClientPicker uid={ownerUid} isAdmin={userRole === 'Admin'} viewAll={isTC} onPick={(p) => { setPicked(p); setFormData(prev => ({ ...prev, ...p.fields })); if (p.additionalContacts?.length) setAdditionalContacts((prev) => (prev.length ? prev : p.additionalContacts.map((c, n) => ({ id: Date.now() + n, firstName: c.firstName, lastName: c.lastName, role: c.relationship, phone: c.phone, email: c.email, signer: true })))); }} />}

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>First Name *</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="John"
                  style={{...styles.input, borderColor: errors.firstName ? '#d32f2f' : '#DDD'}}
                />
                {errors.firstName && <p style={styles.errorText}>{errors.firstName}</p>}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Last Name *</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Smith"
                  style={{...styles.input, borderColor: errors.lastName ? '#d32f2f' : '#DDD'}}
                />
                {errors.lastName && <p style={styles.errorText}>{errors.lastName}</p>}
              </div>
            </div>

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  style={{...styles.input, borderColor: errors.email ? '#d32f2f' : '#DDD'}}
                />
                {errors.email && <p style={styles.errorText}>{errors.email}</p>}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Phone *</label>
                <PhoneInput
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="239-555-0123"
                  style={{...styles.input, borderColor: errors.phone ? '#d32f2f' : '#DDD'}}
                />
                {errors.phone && <p style={styles.errorText}>{errors.phone}</p>}
              </div>
            </div>
          </div>

          {/* Additional Contacts Section */}
          <div style={styles.section}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <h2 style={styles.sectionTitle}>Additional Contacts</h2>
              <button
                type="button"
                onClick={addContact}
                style={{padding: '8px 16px', backgroundColor: '#D4AF37', color: '#000', border: 'none', borderRadius: '4px', fontWeight: '600', cursor: 'pointer'}}
              >
                + Add Contact
              </button>
            </div>

            {additionalContacts.length === 0 && (
              <p style={styles.subtitle}>No additional contacts yet. Add co-sellers, a spouse, an attorney, or anyone else connected to the listing.</p>
            )}

            {additionalContacts.map((contact, idx) => (
              <div key={contact.id} style={{border: '1px solid #DDD', borderRadius: '6px', padding: '16px', marginBottom: '16px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                  <span style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                  <strong>Contact {idx + 2}</strong>
                  <label style={{display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 400, cursor: 'pointer'}} title="Uncheck if this person does not sign. They still get the transaction emails.">
                    <input type="checkbox" checked={contact.signer !== false} onChange={(e) => updateContact(contact.id, 'signer', e.target.checked)} /> Signer
                  </label>
                </span>
                  <button
                    type="button"
                    onClick={() => removeContact(contact.id)}
                    style={{background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontWeight: '600'}}
                  >
                    Remove
                  </button>
                </div>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>First Name</label>
                    <input type="text" value={contact.firstName} onChange={(e) => updateContact(contact.id, 'firstName', e.target.value)} style={styles.input} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Last Name</label>
                    <input type="text" value={contact.lastName} onChange={(e) => updateContact(contact.id, 'lastName', e.target.value)} style={styles.input} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Role / Relationship</label>
                    <input type="text" value={contact.role} onChange={(e) => updateContact(contact.id, 'role', e.target.value)} placeholder="Co-seller, spouse, attorney, etc." style={styles.input} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Phone</label>
                    <PhoneInput value={contact.phone} onChange={(e) => updateContact(contact.id, 'phone', e.target.value)} style={styles.input} />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Email</label>
                    <input type="email" value={contact.email} onChange={(e) => updateContact(contact.id, 'email', e.target.value)} style={styles.input} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Co-List Agents Section */}
          <div style={styles.section}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <h2 style={styles.sectionTitle}>Co-List Agents</h2>
              <button
                type="button"
                onClick={addCoListAgent}
                style={{padding: '8px 16px', backgroundColor: '#D4AF37', color: '#000', border: 'none', borderRadius: '4px', fontWeight: '600', cursor: 'pointer'}}
              >
                + Add Co-List Agent
              </button>
            </div>

            {coListAgents.length === 0 && (
              <p style={styles.subtitle}>No co-list agent on this listing.</p>
            )}

            {coListAgents.map((coAgent, idx) => (
              <div key={coAgent.id} style={{display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '12px'}}>
                <div style={{...styles.formGroup, flex: 1}}>
                  <label style={styles.label}>Co-List Agent {idx + 1}</label>
                  <select value={coAgent.uid} onChange={(e) => updateCoListAgent(coAgent.id, e.target.value)} style={styles.input}>
                    <option value="">Select an agent</option>
                    {agents.map(agent => (
                      <option key={agent.uid} value={agent.uid}>{agent.displayName}</option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => removeCoListAgent(coAgent.id)}
                  style={{background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontWeight: '600', paddingBottom: '10px'}}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          {/* Property Information Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Property Information</h2>

            <div style={styles.formGroup}>
              <label style={styles.label}>Street Address *</label>
              <input
                type="text"
                name="propertyAddress"
                value={formData.propertyAddress}
                onChange={handleChange}
                placeholder="123 Main St"
                style={{...styles.input, borderColor: errors.propertyAddress ? '#d32f2f' : '#DDD'}}
              />
              {errors.propertyAddress && <p style={styles.errorText}>{errors.propertyAddress}</p>}
            </div>

            {/* Community: drives the Listing Launch brochure (The Place / Verdana / general). Needed before Listing Launch creates the brochure. */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Community (needed for Listing Launch)</label>
              {(() => {
                const KNOWN = ['The Place at Corkscrew', 'Verdana Village'];
                const val = formData.community || '';
                const isOther = communityOther || (val && !KNOWN.includes(val));
                return (
                  <>
                    <select style={styles.input} value={isOther ? '__other' : val}
                      onChange={(e) => { const v = e.target.value; if (v === '__other') { setCommunityOther(true); setFormData((f) => ({ ...f, community: KNOWN.includes(f.community) ? '' : f.community })); } else { setCommunityOther(false); setFormData((f) => ({ ...f, community: v })); } }}>
                      <option value="">Select community</option>
                      {KNOWN.map((c) => <option key={c} value={c}>{c}</option>)}
                      <option value="__other">Other (type community name)</option>
                    </select>
                    {isOther && <input type="text" style={{ ...styles.input, marginTop: 6 }} placeholder="Community name" value={val} onChange={(e) => setFormData((f) => ({ ...f, community: e.target.value }))} />}
                  </>
                );
              })()}
            </div>

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>City</label>
                <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="Estero" style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>State</label>
                <input type="text" name="state" value={formData.state} onChange={handleChange} placeholder="FL" style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Zip Code</label>
                <input type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} placeholder="33928" style={styles.input} />
              </div>
            </div>

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Property Type</label>
                <select name="propertyType" value={formData.propertyType} onChange={handleChange} style={styles.input}>
                  <option value="single-family">Single Family Home</option>
                  <option value="condo">Condo</option>
                  <option value="townhome">Townhome</option>
                  <option value="multi-family">Multi-Family</option>
                  <option value="other">Other</option>
                </select>
              </div>

            </div>

          </div>

          {/* Listing Information Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Listing Information</h2>

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Listing Price *</label>
                <MoneyInput
                  name="listingPrice"
                  value={formData.listingPrice}
                  onChange={handleChange}
                  placeholder="$750,000"
                  style={{...styles.input, borderColor: errors.listingPrice ? '#d32f2f' : '#DDD'}}
                />
                {errors.listingPrice && <p style={styles.errorText}>{errors.listingPrice}</p>}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="cddAnnual">CDD</label>
                <MoneyInput id="cddAnnual" name="cddAnnual" value={formData.cddAnnual || ''} onChange={handleChange} placeholder="$2,450" style={styles.input} />
                <p style={{ fontSize: 12, color: '#666', margin: '4px 0 0' }}>Annual CDD (non-ad valorem) from the county tax bill</p>
              </div>


              <div style={styles.formGroup}>
                <label style={styles.label}>Bedrooms</label>
                <input type="number" min="0" step="1" inputMode="numeric" name="bedrooms" value={formData.bedrooms ?? ''} onChange={handleChange} placeholder="3" style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Full baths</label>
                <input type="number" min="0" step="1" inputMode="numeric" name="fullBaths" value={formData.fullBaths ?? ''} onChange={handleChange} placeholder="2" style={styles.input} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Half baths</label>
                <input type="number" min="0" step="1" inputMode="numeric" name="halfBaths" value={formData.halfBaths ?? ''} onChange={handleChange} placeholder="0" style={styles.input} />
              </div>
              <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                <label style={styles.label}>Extra rooms (check only if the home has one)</label>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  {[['hasDen', 'Den'], ['hasFlex', 'Flex room'], ['hasLoft', 'Loft']].map(([k, l]) => (
                    <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 44, cursor: 'pointer' }}>
                      <input type="checkbox" name={k} checked={!!formData[k]} onChange={handleChange} style={{ width: 20, height: 20 }} /> {l}
                    </label>
                  ))}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Anticipated Listing Date *</label>
                <input
                  type="date"
                  name="anticipatedListingDate"
                  value={formData.anticipatedListingDate}
                  onChange={handleChange}
                  style={styles.input}
                />
                {errors.anticipatedListingDate && (
                  <div style={{ color: '#b00020', fontSize: 12, marginTop: 4 }}>{errors.anticipatedListingDate}</div>
                )}
              </div>

            </div>

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Listing Agreement Effective Date *</label>
                <input
                  type="date"
                  name="listingEffectiveDate"
                  value={formData.listingEffectiveDate}
                  onChange={handleChange}
                  style={styles.input}
                />
                {errors.listingEffectiveDate && (
                  <div style={{ color: '#b00020', fontSize: 12, marginTop: 4 }}>{errors.listingEffectiveDate}</div>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Listing Expiration Date *</label>
                <input
                  type="date"
                  name="listingExpirationDate"
                  value={formData.listingExpirationDate}
                  onChange={handleChange}
                  style={styles.input}
                />
                {errors.listingExpirationDate && (
                  <div style={{ color: '#b00020', fontSize: 12, marginTop: 4 }}>{errors.listingExpirationDate}</div>
                )}
              </div>
            </div>

          </div>

          {/* Staging & Photography Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Staging & Photography</h2>

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Staging Needed?</label>
                <select name="stagingNeeded" value={formData.stagingNeeded} onChange={handleChange} style={styles.input}>
                  <option value="no">No</option>
                  <option value="virtual">Virtual Only</option>
                  <option value="partial">Some Pieces (by Agent)</option>
                  <option value="professional">Professional Staging</option>
                </select>
              </div>

              {formData.stagingNeeded === 'professional' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Stager</label>
                  <select name="stager" value={formData.stager} onChange={handleChange} style={styles.input}>
                    <option value="">{stagers.length ? 'Select stager' : 'No stagers yet (add them on the Partners page)'}</option>
                    {stagers.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                    {formData.stager && !stagers.includes(formData.stager) && (
                      <option value={formData.stager}>{formData.stager}</option>
                    )}
                  </select>
                </div>
              )}

              <div style={styles.formGroup}>
                <label style={styles.label}>Photographer</label>
                <select name="photographer" value={formData.photographer} onChange={handleChange} style={styles.input}>
                  <option value="">{photographers.length ? 'Select photographer' : 'No photographers yet (add them on the Partners page)'}</option>
                  {photographers.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                  {formData.photographer && !photographers.includes(formData.photographer) && (
                    <option value={formData.photographer}>{formData.photographer}</option>
                  )}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Requested Photography Date</label>
                <input
                  type="date"
                  name="photographyDate"
                  value={formData.photographyDate}
                  onChange={handleChange}
                  placeholder="Preferred shoot date"
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Notes to Photographer</label>
              <textarea
                name="photographerNotes"
                value={formData.photographerNotes}
                onChange={handleChange}
                placeholder="Shots to prioritize, areas to avoid, lighting or timing notes..."
                rows="2"
                style={styles.textarea}
              />
            </div>

          </div>

          {/* Marketing Materials Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Marketing Materials</h2>

            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="createBrochure"
                  checked={formData.createBrochure}
                  onChange={handleChange}
                  style={{marginRight: '8px'}}
                />
                Create Brochure
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="createMailer"
                  checked={formData.createMailer}
                  onChange={handleChange}
                  style={{marginRight: '8px'}}
                />
                Create Mailer
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="createFeatureCards"
                  checked={formData.createFeatureCards}
                  onChange={handleChange}
                  style={{marginRight: '8px'}}
                />
                Create Feature Cards
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="createFeatureList"
                  checked={formData.createFeatureList}
                  onChange={handleChange}
                  style={{marginRight: '8px'}}
                />
                Create Feature List
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="createSocialGraphics"
                  checked={formData.createSocialGraphics}
                  onChange={handleChange}
                  style={{marginRight: '8px'}}
                />
                Create Social Graphics
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="createJustSoldPostcards"
                  checked={formData.createJustSoldPostcards === true}
                  onChange={handleChange}
                  style={{marginRight: '8px'}}
                />
                Just Sold Postcards (task starts when the home closes)
              </label>
            </div>


            <div style={styles.formGroup}>
              <label style={styles.label}>Key Features to Highlight</label>
              <textarea
                name="keyFeatures"
                value={formData.keyFeatures}
                onChange={handleChange}
                placeholder="Pool, gated community, lake view, outdoor kitchen, impact windows, etc."
                rows="3"
                style={styles.textarea}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Notes After Touring the Home (for the Marketing Manager)</label>
              <textarea
                name="tourNotes"
                value={formData.tourNotes || formData.listingNotes || ''}
                onChange={(e) => { const v = e.target.value; setFormData((p) => ({ ...p, tourNotes: v, listingNotes: v })); }}
                placeholder="Brain dump: finishes, upgrades, recent updates, the best views and rooms, neighborhood details, anything the seller said, what made you stop and look twice. Copy and paste it wherever it needs to go."
                rows="12"
                style={{ ...styles.textarea, minHeight: 240 }}
              />
              <div style={{ marginTop: 8 }}>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard?.writeText(formData.tourNotes || formData.listingNotes || '').catch(() => {}); }}
                  style={{ padding: '8px 14px', borderRadius: 6, border: 'none', background: '#1a1a1a', color: '#D4AF37', fontWeight: 600, cursor: 'pointer' }}
                >
                  Copy notes
                </button>
              </div>
            </div>

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Marketing Theme/Angle</label>
                <input
                  type="text"
                  name="marketingTheme"
                  value={formData.marketingTheme}
                  onChange={handleChange}
                  placeholder="e.g., 'Resort-style living', 'Golf community gem', 'Builder showcase'"
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Rearview Direction</label>
                <select name="rearDirection" value={formData.rearDirection} onChange={handleChange} style={styles.input}>
                  <option value="">Select direction</option>
                  <option value="north">North</option>
                  <option value="north-northeast">North Northeast</option>
                  <option value="northeast">Northeast</option>
                  <option value="east-northeast">East Northeast</option>
                  <option value="east">East</option>
                  <option value="east-southeast">East Southeast</option>
                  <option value="southeast">Southeast</option>
                  <option value="south-southeast">South Southeast</option>
                  <option value="south">South</option>
                  <option value="south-southwest">South Southwest</option>
                  <option value="southwest">Southwest</option>
                  <option value="west-southwest">West Southwest</option>
                  <option value="west">West</option>
                  <option value="west-northwest">West Northwest</option>
                  <option value="northwest">Northwest</option>
                  <option value="north-northwest">North Northwest</option>
                  <option value="unknown">Not sure</option>
                </select>
              </div>
            </div>

          </div>

          {/* Showing Instructions Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Showing Instructions</h2>

            <div style={styles.formGroup}>
              <label style={styles.label}>Occupied or Vacant?</label>
              <select name="occupancyStatus" value={formData.occupancyStatus} onChange={handleChange} style={styles.input}>
                <option value="">Select</option>
                <option value="occupied">Occupied</option>
                <option value="vacant">Vacant</option>
              </select>
            </div>


            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Access Instructions</label>
                <textarea
                  name="accessInstructions"
                  value={formData.accessInstructions}
                  onChange={handleChange}
                  placeholder="Special gate codes, garage openers, alarm codes, etc."
                  rows="2"
                  style={styles.textarea}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Lockbox Code (if applicable)</label>
                <input
                  type="text"
                  name="lockboxCode"
                  value={formData.lockboxCode}
                  onChange={handleChange}
                  placeholder="Lockbox access code"
                  style={styles.input}
                />
              </div>
            </div>
          </div>

          {/* Commission Information Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Commission Information</h2>

            <div style={styles.formGroup}>
              <label style={styles.label}>Commission Type</label>
              <div style={styles.radioGroup}>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="commissionType"
                    value="percentage"
                    checked={formData.commissionType === 'percentage'}
                    onChange={handleChange}
                  />
                  Percentage of Sale Price
                </label>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="commissionType"
                    value="dollar"
                    checked={formData.commissionType === 'dollar'}
                    onChange={handleChange}
                  />
                  Fixed Dollar Amount
                </label>
              </div>
            </div>

            {formData.commissionType === 'percentage' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Commission Percentage (%)</label>
                <input
                  type="number"
                  step="0.01"
                  name="commissionPercentage"
                  value={formData.commissionPercentage}
                  onChange={handleChange}
                  placeholder="e.g., 2.5"
                  style={styles.input}
                />
              </div>
            )}

            {formData.commissionType === 'dollar' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Commission Amount ($)</label>
                <MoneyInput
                  step="0.01"
                  name="commissionDollarAmount"
                  value={formData.commissionDollarAmount}
                  onChange={handleChange}
                  placeholder="e.g., 12500"
                  style={styles.input}
                />
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Lead Generated By{userRole !== 'Admin' && <span style={{ fontWeight: 400, fontSize: 12, color: '#7f7f7f' }}> (set by an admin)</span>}</label>
              <div style={styles.radioGroup}>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="leadSource"
                    value="agent-generated"
                    checked={formData.leadSource === 'agent-generated'}
                    onChange={handleChange}
                  disabled={userRole !== 'Admin'} /* only admins change Lead generated by */
                  />
                  Agent
                </label>
                <label style={styles.radioLabel}>
                  <input
                    type="radio"
                    name="leadSource"
                    value="team-generated"
                    checked={formData.leadSource === 'team-generated'}
                    onChange={handleChange}
                  disabled={userRole !== 'Admin'} /* only admins change Lead generated by */
                  />
                  Team
                </label>
              </div>
            </div>

            <SourceSelect formData={formData} onChange={handleChange} styles={styles} />
          </div>

          {/* Condo Documents Section (condo properties only) */}
          {formData.propertyType === 'condo' && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Condo Document Delivery Checklist</h2>
              <p style={{margin: '0 0 8px', fontSize: '14px', color: '#666'}}>{condoDocuments.filter(d => d.key === 'governanceForm' || documentFiles[d.key] || notApplicable[d.key]).length} of {condoDocuments.length} received or N/A</p>
              <div>
                {condoDocuments.map(doc => (
                  <div key={doc.key} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #EEE'}}>
                    <input type="checkbox" readOnly tabIndex={-1} checked={doc.key === 'governanceForm' || !!documentFiles[doc.key] || !!notApplicable[doc.key]} style={{marginRight: '12px', width: '18px', height: '18px', accentColor: '#D4AF37', flexShrink: 0}} />
                    <span style={{flex: 1, paddingRight: '12px'}}>{doc.label}{doc.note && <div style={{fontSize: '12px', color: '#777', marginTop: 4, lineHeight: 1.4}}>{doc.note}</div>}</span>
                    <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                      <span style={{fontSize: '13px', color: '#666'}}>{doc.key === 'governanceForm' ? 'Included' : notApplicable[doc.key] ? 'Not applicable' : (documentFiles[doc.key] || 'No file')}</span>
                      {NA_KEYS.includes(doc.key) && (
                        <button type="button" onClick={() => setNotApplicable(prev => ({ ...prev, [doc.key]: !prev[doc.key] }))} style={{padding: '6px 12px', borderRadius: '4px', border: '1px solid #1a1a1a', background: notApplicable[doc.key] ? '#fff' : '#1a1a1a', color: notApplicable[doc.key] ? '#1a1a1a' : '#D4AF37', fontWeight: '600', cursor: 'pointer'}}>
                          {notApplicable[doc.key] ? 'Undo N/A' : 'Not applicable'}
                        </button>
                      )}
                      {doc.key === 'governanceForm' ? (
                        <a href="/condo-governance-form.pdf" target="_blank" rel="noopener noreferrer" style={{padding: '6px 14px', backgroundColor: '#D4AF37', color: '#000', borderRadius: '4px', fontWeight: '600', textDecoration: 'none'}}>
                          Open form
                        </a>
                      ) : notApplicable[doc.key] ? null : (
                      <label style={{padding: '6px 14px', backgroundColor: '#D4AF37', color: '#000', borderRadius: '4px', fontWeight: '600', cursor: 'pointer'}}>
                        Upload
                        <input type="file" style={{display: 'none'}} onChange={(e) => handleDocumentUpload(doc.key, e.target.files[0])} />
                      </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Association Approval: required Yes/No, no default. Drives the buyer HOA/condo approval tasks. */}
          <fieldset style={{ border: "none", padding: 0, margin: "0 0 16px" }}>
            <legend style={styles.sectionTitle}>Association Approval</legend>
            <p style={styles.label}>Is association approval required for this listing?</p>
            <div style={{ display: 'flex', gap: '24px' }}>
              <label style={styles.checkboxLabel}>
                <input type="radio" name="associationApprovalRequired" value="Yes" checked={formData.associationApprovalRequired === 'Yes'} onChange={handleChange} required />
                Yes
              </label>
              <label style={styles.checkboxLabel}>
                <input type="radio" name="associationApprovalRequired" value="No" checked={formData.associationApprovalRequired === 'No'} onChange={handleChange} required />
                No
              </label>
            </div>
          </fieldset>

          {/* HOA open house notice: required Yes/No, no default. If Yes, the HOA contact is required. */}
          <fieldset style={{ border: "none", padding: 0, margin: "0 0 16px" }}>
            <legend style={styles.sectionTitle}>HOA Open House Notice</legend>
            <p style={styles.label}>Do we need to notify the HOA of open houses for this listing?</p>
            <div style={{ display: 'flex', gap: '24px' }}>
              <label style={styles.checkboxLabel}>
                <input type="radio" name="notifyHoaOfOpenHouses" value="Yes" checked={formData.notifyHoaOfOpenHouses === 'Yes'} onChange={handleChange} required />
                Yes
              </label>
              <label style={styles.checkboxLabel}>
                <input type="radio" name="notifyHoaOfOpenHouses" value="No" checked={formData.notifyHoaOfOpenHouses === 'No'} onChange={handleChange} required />
                No
              </label>
            </div>
            {formData.notifyHoaOfOpenHouses === 'Yes' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginTop: '12px' }}>
                <div>
                  <label style={styles.label} htmlFor="hoaContactFirstName">HOA contact first name</label>
                  <input id="hoaContactFirstName" type="text" name="hoaContactFirstName" style={styles.input} value={formData.hoaContactFirstName} onChange={handleChange} required />
                </div>
                <div>
                  <label style={styles.label} htmlFor="hoaContactLastName">HOA contact last name</label>
                  <input id="hoaContactLastName" type="text" name="hoaContactLastName" style={styles.input} value={formData.hoaContactLastName} onChange={handleChange} required />
                </div>
                <div>
                  <label style={styles.label} htmlFor="hoaContactEmail">HOA contact email</label>
                  <input id="hoaContactEmail" type="email" name="hoaContactEmail" style={styles.input} value={formData.hoaContactEmail} onChange={handleChange} required />
                </div>
              </div>
            )}
          </fieldset>


          {/* Additional Notes Section */}
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Additional Information</h2>

            <div style={styles.formGroup}>
              <label style={styles.label}>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Any additional information or special notes..."
                rows="4"
                style={styles.textarea}
              />
            </div>
          </div>

          {/* Form Actions */}
          <div style={styles.formActions}>
            <button
              type="submit"
              style={{...styles.submitButton, opacity: loading ? 0.6 : 1}}
              disabled={loading}
            >
              <CheckCircle size={18} /> {loading ? 'Saving...' : (isEdit && !wasDraft ? 'Save Changes' : 'Create Seller Transaction')}
            </button>
            {(!isEdit || wasDraft) && (
              <button type="button" onClick={saveDraft} style={{ ...styles.cancelButton, opacity: draftSaving ? 0.6 : 1 }} disabled={loading || draftSaving}>
                {draftSaving ? 'Saving draft...' : 'Save as draft'}
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/transactions')}
              style={styles.cancelButton}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  header: {
    backgroundColor: '#F5F5F5',
    borderBottom: '1px solid #E0E0E0',
    padding: '20px 40px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#666',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '8px 12px',
    borderRadius: '4px',
    transition: 'all 0.3s ease'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#000',
    margin: '0'
  },
  mainContent: {
    padding: '40px',
    maxWidth: '900px',
    margin: '0 auto'
  },
  successBox: {
    backgroundColor: '#E8F5E9',
    border: '1px solid #4CAF50',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start'
  },
  successTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#2E7D32',
    margin: '0 0 4px 0'
  },
  successSubtitle: {
    fontSize: '14px',
    color: '#558B2F',
    margin: '0'
  },
  errorBox: {
    backgroundColor: '#FFEBEE',
    border: '1px solid #d32f2f',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start'
  },
  errorTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#C62828',
    margin: '0 0 4px 0'
  },
  errorSubtitle: {
    fontSize: '14px',
    color: '#B71C1C',
    margin: '0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px'
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#000',
    margin: '0 0 16px 0',
    paddingBottom: '12px',
    borderBottom: '2px solid #D4AF37'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  checkboxGroup: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    marginBottom: '16px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#333',
    cursor: 'pointer'
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#333',
    cursor: 'pointer'
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  input: {
    padding: '12px',
    border: '1px solid #DDD',
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    boxSizing: 'border-box',
    transition: 'border-color 0.3s ease'
  },
  textarea: {
    padding: '12px',
    border: '1px solid #DDD',
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    boxSizing: 'border-box',
    resize: 'vertical',
    transition: 'border-color 0.3s ease'
  },
  errorText: {
    fontSize: '12px',
    color: '#d32f2f',
    margin: '0'
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    paddingTop: '16px',
    borderTop: '1px solid #E0E0E0'
  },
  submitButton: {
    flex: '1',
    padding: '14px 24px',
    backgroundColor: '#4CAF50',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '4px',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  cancelButton: {
    padding: '14px 24px',
    backgroundColor: '#FFFFFF',
    color: '#666',
    border: '1px solid #DDD',
    borderRadius: '4px',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  }
};
